// Generated by scripts/generate-planner-contracts.mjs; do not edit.
// Backend schema SHA256: d05ca602bf6f27eed56de08457ffcda577563a4dc750a9ad2f85e9370bc816ab
// Structural validation only. Conditional/cross-field rules are enforced by Java.
import { z } from 'zod'

export const plannerWarningSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  severity: z.enum(['info', 'warning']),
  target_id: z.union([z.string().min(1), z.null()]),
})
export type PlannerWarning = z.infer<typeof plannerWarningSchema>

export const plannerEvidenceSchema = z.object({
  kind: z.enum(['supplier', 'model', 'user', 'derived']),
  provider: z.union([z.string().min(1), z.null()]),
  api: z.union([z.string().min(1), z.null()]),
  observed_at: z.union([z.string().datetime({ offset: true }).regex(new RegExp('Z$')), z.null()]),
  candidate_ref: z.union([z.string().min(1), z.null()]),
})
export type PlannerEvidence = z.infer<typeof plannerEvidenceSchema>

export const plannerOverviewModuleSchema = z.object({
  module_id: z.string().min(1),
  title: z.string().min(1),
  warnings: z.array(plannerWarningSchema),
  evidence: z.array(plannerEvidenceSchema),
  cost_refs: z.array(z.string().min(1)),
  type: z.literal('overview'),
  summary: z.string().min(1),
  highlights: z.array(z.string().min(1)),
  tips: z.array(z.string().min(1)),
})
export type PlannerOverviewModule = z.infer<typeof plannerOverviewModuleSchema>

export const plannerLocalTimeSchema = z.object({
  local_datetime: z
    .string()
    .regex(new RegExp('^\\d{4}-\\d{2}-\\d{2}T([01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d$')),
  timezone: z.union([z.string().min(1), z.null()]),
})
export type PlannerLocalTime = z.infer<typeof plannerLocalTimeSchema>

export const plannerFlightSegmentSchema = z.object({
  segment_id: z.string().min(1),
  index: z.number().int().min(1).max(9007199254740991),
  departure_code: z.string().min(1),
  departure_name: z.union([z.string().min(1), z.null()]),
  departure_terminal: z.union([z.string().min(1), z.null()]),
  arrival_code: z.string().min(1),
  arrival_name: z.union([z.string().min(1), z.null()]),
  arrival_terminal: z.union([z.string().min(1), z.null()]),
  departure: plannerLocalTimeSchema,
  arrival: plannerLocalTimeSchema,
  elapsed_minutes: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  marketing_airline_code: z.string().min(1),
  marketing_airline_name: z.union([z.string().min(1), z.null()]),
  marketing_flight_number: z.string().min(1),
  operating_airline_code: z.union([z.string().min(1), z.null()]),
  operating_flight_number: z.union([z.string().min(1), z.null()]),
  equipment: z.union([z.string().min(1), z.null()]),
  stop_count: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  cabin_code: z.union([z.string().min(1), z.null()]),
})
export type PlannerFlightSegment = z.infer<typeof plannerFlightSegmentSchema>

export const plannerFlightJourneySchema = z.object({
  journey_id: z.string().min(1),
  elapsed_minutes: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  segments: z.array(plannerFlightSegmentSchema).min(1),
})
export type PlannerFlightJourney = z.infer<typeof plannerFlightJourneySchema>

export const plannerFlightSelectionSchema = z.object({
  candidate_ref: z.string().min(1),
  journeys: z.array(plannerFlightJourneySchema).min(1).max(1),
  cabin_level: z.enum(['unspecified', 'economy', 'business', 'first']),
  cabin_name: z.union([z.string().min(1), z.null()]),
  fare_notes: z.array(z.string().min(1)),
  baggage_notes: z.array(z.string().min(1)),
  queried_at: z.string().datetime({ offset: true }).regex(new RegExp('Z$')),
  expires_at: z.union([z.string().datetime({ offset: true }).regex(new RegExp('Z$')), z.null()]),
})
export type PlannerFlightSelection = z.infer<typeof plannerFlightSelectionSchema>

export const plannerTransportModuleSchema = z.object({
  module_id: z.string().min(1),
  title: z.string().min(1),
  warnings: z.array(plannerWarningSchema),
  evidence: z.array(plannerEvidenceSchema),
  cost_refs: z.array(z.string().min(1)),
  type: z.literal('transport'),
  mode: z.literal('flight'),
  role: z.enum(['outbound', 'intercity', 'return']),
  from_city_stay_id: z.union([z.string().min(1), z.null()]),
  to_city_stay_id: z.union([z.string().min(1), z.null()]),
  status: z.enum(['selected', 'pending', 'not_needed']),
  reason_code: z.union([z.string().min(1), z.null()]),
  flight: z.union([plannerFlightSelectionSchema, z.null()]),
})
export type PlannerTransportModule = z.infer<typeof plannerTransportModuleSchema>

export const plannerCoordinatesSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  crs: z.enum(['WGS84', 'GCJ02', 'BD09', 'UNCONFIRMED']),
})
export type PlannerCoordinates = z.infer<typeof plannerCoordinatesSchema>

export const plannerRatingSchema = z.object({ value: z.number().min(0), scale: z.number().gt(0) })
export type PlannerRating = z.infer<typeof plannerRatingSchema>

export const plannerHotelIdentitySchema = z.object({
  provider: z.string().min(1),
  hotel_id: z.string().min(1),
  name: z.string().min(1),
  address: z.union([z.string().min(1), z.null()]),
  image_url: z.union([z.string(), z.null()]),
  coordinates: z.union([plannerCoordinatesSchema, z.null()]),
  category: z.enum(['unspecified', 'budget', 'economy', 'comfort', 'premium', 'luxury']),
  category_label: z.union([z.string().min(1), z.null()]),
  is_official_star: z.union([z.boolean(), z.null()]),
  rating: z.union([plannerRatingSchema, z.null()]),
  amenities: z.array(z.string().min(1)),
})
export type PlannerHotelIdentity = z.infer<typeof plannerHotelIdentitySchema>

export const plannerMoneySchema = z.object({
  currency: z.string().regex(new RegExp('^[A-Z]{3}$')),
  amount_minor: z.number().int().min(0).max(9007199254740991),
})
export type PlannerMoney = z.infer<typeof plannerMoneySchema>

export const plannerNightPriceSchema = z.object({
  date: z.string().date(),
  price_per_room: plannerMoneySchema,
})
export type PlannerNightPrice = z.infer<typeof plannerNightPriceSchema>

export const plannerRoomSelectionSchema = z.object({
  candidate_ref: z.string().min(1),
  room_name: z.string().min(1),
  bed_type: z.union([z.string().min(1), z.null()]),
  capacity_text: z.union([z.string().min(1), z.null()]),
  rate_name: z.union([z.string().min(1), z.null()]),
  breakfast_text: z.union([z.string().min(1), z.null()]),
  payment_type: z.enum(['unspecified', 'prepay', 'pay_at_hotel']),
  cancellation_text: z.union([z.string().min(1), z.null()]),
  free_cancel_deadline: z.union([plannerLocalTimeSchema, z.null()]),
  instant_confirm: z.union([z.boolean(), z.null()]),
  average_per_room_night: z.union([plannerMoneySchema, z.null()]),
  nightly_prices: z.array(plannerNightPriceSchema),
  queried_at: z.string().datetime({ offset: true }).regex(new RegExp('Z$')),
  expires_at: z.union([z.string().datetime({ offset: true }).regex(new RegExp('Z$')), z.null()]),
})
export type PlannerRoomSelection = z.infer<typeof plannerRoomSelectionSchema>

export const plannerHotelSelectionSchema = z.object({
  candidate_ref: z.string().min(1),
  hotel: plannerHotelIdentitySchema,
  starting_per_room_night: z.union([plannerMoneySchema, z.null()]),
  room: z.union([plannerRoomSelectionSchema, z.null()]),
  queried_at: z.string().datetime({ offset: true }).regex(new RegExp('Z$')),
})
export type PlannerHotelSelection = z.infer<typeof plannerHotelSelectionSchema>

export const plannerStayModuleSchema = z.object({
  module_id: z.string().min(1),
  title: z.string().min(1),
  warnings: z.array(plannerWarningSchema),
  evidence: z.array(plannerEvidenceSchema),
  cost_refs: z.array(z.string().min(1)),
  type: z.literal('stay'),
  city_stay_id: z.string().min(1),
  check_in_date: z.string().date(),
  check_out_date: z.string().date(),
  room_count: z.union([z.number().int().min(1).max(9007199254740991), z.null()]),
  status: z.enum(['selected', 'pending', 'not_needed']),
  reason_code: z.union([z.string().min(1), z.null()]),
  selection: z.union([plannerHotelSelectionSchema, z.null()]),
})
export type PlannerStayModule = z.infer<typeof plannerStayModuleSchema>

export const plannerActivityInputSchema = z.object({
  name: z.string().min(1),
  category: z.enum(['sightseeing', 'food', 'culture', 'shopping', 'rest', 'other']),
  description: z.string().min(1),
  address: z.union([z.string().min(1), z.null()]),
  coordinates: z.union([plannerCoordinatesSchema, z.null()]),
  time: z.object({
    kind: z.enum(['flexible', 'fixed']),
    start: z.union([z.string().regex(new RegExp('^([01]\\d|2[0-3]):[0-5]\\d$')), z.null()]),
    end: z.union([z.string().regex(new RegExp('^([01]\\d|2[0-3]):[0-5]\\d$')), z.null()]),
    duration_minutes: z.union([z.number().int().min(1).max(9007199254740991), z.null()]),
  }),
  tips: z.array(z.string().min(1)),
})
export type PlannerActivityInput = z.infer<typeof plannerActivityInputSchema>

export const plannerActivitySchema = z.object({
  activity_id: z.string().min(1),
  content: plannerActivityInputSchema,
  verification: z.object({
    opening_hours: z.enum(['unverified', 'verified', 'stale']),
    reservation: z.enum(['unverified', 'verified', 'stale']),
  }),
  evidence: z.array(plannerEvidenceSchema),
  cost_refs: z.array(z.string().min(1)),
})
export type PlannerActivity = z.infer<typeof plannerActivitySchema>

export const plannerConnectionSchema = z.object({
  connection_id: z.string().min(1),
  from_item_id: z.string().min(1),
  to_item_id: z.string().min(1),
  mode: z.enum(['unknown', 'walk', 'public_transport', 'taxi', 'other']),
  duration_minutes: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  description: z.union([z.string().min(1), z.null()]),
  status: z.enum(['unknown', 'estimated', 'verified', 'stale']),
  evidence: z.array(plannerEvidenceSchema),
})
export type PlannerConnection = z.infer<typeof plannerConnectionSchema>

export const plannerDayModuleSchema = z.object({
  module_id: z.string().min(1),
  title: z.string().min(1),
  warnings: z.array(plannerWarningSchema),
  evidence: z.array(plannerEvidenceSchema),
  cost_refs: z.array(z.string().min(1)),
  type: z.literal('day'),
  city_stay_id: z.string().min(1),
  date: z.string().date(),
  day_index: z.number().int().min(1).max(9007199254740991),
  activities: z.array(plannerActivitySchema),
  connections: z.array(plannerConnectionSchema),
})
export type PlannerDayModule = z.infer<typeof plannerDayModuleSchema>

export const plannerCostItemSchema = z.object({
  cost_id: z.string().min(1),
  label: z.string().min(1),
  status: z.enum(['observed', 'estimated', 'unknown', 'not_applicable']),
  total: z.union([plannerMoneySchema, z.null()]),
  scope: z.enum(['party', 'room_stay', 'activity']),
  quantity: z.union([z.number().int().min(1).max(9007199254740991), z.null()]),
  coverage_module_ids: z.array(z.string().min(1)),
  evidence: z.array(plannerEvidenceSchema),
})
export type PlannerCostItem = z.infer<typeof plannerCostItemSchema>

export const plannerCurrencySubtotalSchema = z.object({
  currency: z.string().regex(new RegExp('^[A-Z]{3}$')),
  observed_minor: z.number().int().min(0).max(9007199254740991),
  estimated_minor: z.number().int().min(0).max(9007199254740991),
})
export type PlannerCurrencySubtotal = z.infer<typeof plannerCurrencySubtotalSchema>

export const plannerCostSummarySchema = z.object({
  coverage: z.enum(['complete', 'partial', 'unavailable']),
  subtotals: z.array(plannerCurrencySubtotalSchema),
  unknown_cost_ids: z.array(z.string().min(1)),
  disclaimer: z.string().min(1),
})
export type PlannerCostSummary = z.infer<typeof plannerCostSummarySchema>

export const plannerReportSchema = z.object({
  schema_version: z.literal('planner_report_v2'),
  title: z.string().min(1).max(200),
  modules: z.array(
    z.union([
      plannerOverviewModuleSchema,
      plannerTransportModuleSchema,
      plannerStayModuleSchema,
      plannerDayModuleSchema,
    ]),
  ),
  cost_items: z.array(plannerCostItemSchema),
  cost_summary: plannerCostSummarySchema,
  warnings: z.array(plannerWarningSchema),
})
export type PlannerReport = z.infer<typeof plannerReportSchema>

export const plannerPlaceSchema = z.object({
  name: z.string().min(1),
  country_code: z.union([z.string().regex(new RegExp('^[A-Z]{2}$')), z.null()]),
  city_code: z.union([z.string().min(1), z.null()]),
  code_system: z.union([z.string().min(1), z.null()]),
  timezone: z.union([z.string().min(1), z.null()]),
  coordinates: z.union([plannerCoordinatesSchema, z.null()]),
})
export type PlannerPlace = z.infer<typeof plannerPlaceSchema>

export const plannerTravelersSchema = z.object({
  total: z.number().int().min(1).max(9007199254740991),
  adults: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  children: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  infants: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  child_ages: z.array(z.number().int().min(0).max(17)),
})
export type PlannerTravelers = z.infer<typeof plannerTravelersSchema>

export const plannerCityStaySchema = z.object({
  city_stay_id: z.string().min(1),
  city: plannerPlaceSchema,
  start_date: z.string().date(),
  end_date: z.string().date(),
})
export type PlannerCityStay = z.infer<typeof plannerCityStaySchema>

export const plannerAssumptionSchema = z.object({
  path: z.string().min(1),
  value_text: z.string().min(1),
  source: z.enum(['user', 'default', 'inherited', 'derived']),
  reason: z.string().min(1),
})
export type PlannerAssumption = z.infer<typeof plannerAssumptionSchema>

export const plannerBriefSchema = z.object({
  schema_version: z.literal('planner_brief_v2'),
  origin: z.union([plannerPlaceSchema, z.null()]),
  start_date: z.string().date(),
  end_date: z.string().date(),
  duration_days: z.number().int().min(1).max(9007199254740991),
  reference_timezone: z.string().min(1),
  travelers: plannerTravelersSchema,
  room_count: z.union([z.number().int().min(1).max(9007199254740991), z.null()]),
  city_stays: z.array(plannerCityStaySchema).min(1),
  preferences: z.object({
    pace: z.enum(['relaxed', 'balanced', 'intensive']),
    interests: z.array(z.string().min(1)),
    comfort: z.enum(['budget', 'comfort', 'premium']),
    budget_total: z.union([plannerMoneySchema, z.null()]),
    notes: z.array(z.string().min(1)),
  }),
  assumptions: z.array(plannerAssumptionSchema),
})
export type PlannerBrief = z.infer<typeof plannerBriefSchema>

export const plannerEnvelopeSchema = z.object({
  planner_id: z.string().min(1),
  version_id: z.string().min(1),
  version_no: z.number().int().min(1).max(9007199254740991),
  revision: z.number().int().min(0).max(9007199254740991),
  report: plannerReportSchema,
  brief: plannerBriefSchema,
})
export type PlannerEnvelope = z.infer<typeof plannerEnvelopeSchema>

export const plannerSummarySchema = z.object({
  title: z.string().min(1).max(200),
  cover_image_url: z.union([z.string(), z.null()]),
  description: z.string().min(1),
  start_date: z.string().date(),
  end_date: z.string().date(),
  duration_days: z.number().int().min(1).max(9007199254740991),
  traveler_count: z.number().int().min(1).max(9007199254740991),
  origin: z.union([plannerPlaceSchema, z.null()]),
  city_count: z.number().int().min(0).max(9007199254740991),
  hotel_count: z.number().int().min(0).max(9007199254740991),
  activity_count: z.number().int().min(0).max(9007199254740991),
  transport_count: z.number().int().min(0).max(9007199254740991),
  pending_transport_count: z.number().int().min(0).max(9007199254740991),
})
export type PlannerSummary = z.infer<typeof plannerSummarySchema>

export const plannerDetailSchema = z.object({
  schema_version: z.literal('planner_detail_v2'),
  data: plannerEnvelopeSchema,
  summary: plannerSummarySchema,
  state: z.object({
    latest_version_id: z.string().min(1),
    is_latest: z.boolean(),
    active_operation_id: z.union([z.string().min(1), z.null()]),
    allowed_actions: z.array(z.enum(['open', 'preview', 'restore', 'edit', 'replan'])),
    evaluated_at: z.string().datetime({ offset: true }).regex(new RegExp('Z$')),
  }),
  market_state: z.array(
    z.object({
      candidate_ref: z.string().min(1),
      freshness: z.enum(['fresh', 'expired', 'unknown']),
    }),
  ),
})
export type PlannerDetail = z.infer<typeof plannerDetailSchema>

export const plannerCardSchema = z.object({
  type: z.literal('planner_report_ref_v2'),
  planner_id: z.string().min(1),
  version_id: z.string().min(1),
  title_snapshot: z.string().min(1),
  cover_image_url: z.union([z.string(), z.null()]),
  operation_id: z.string().min(1),
})
export type PlannerCard = z.infer<typeof plannerCardSchema>

export const plannerActionEntrySchema = z.object({
  type: z.literal('planner_action_v2'),
  action: z.enum(['open_report', 'search_hotels', 'search_flights']),
  planner_id: z.string().min(1),
  version_id: z.string().min(1),
  expected_revision: z.number().int().min(0).max(9007199254740991),
  module_id: z.union([z.string().min(1), z.null()]),
  label: z.string().min(1),
})
export type PlannerActionEntry = z.infer<typeof plannerActionEntrySchema>

export const plannerPublishedEventSchema = z.object({
  type: z.literal('planner.published'),
  event_id: z.string().min(1),
  operation_id: z.string().min(1),
  planner_id: z.string().min(1),
  version_id: z.string().min(1),
  version_no: z.number().int().min(1).max(9007199254740991),
  conversation_id: z.string().min(1),
  message_seq: z.number().int().min(1).max(2147483647),
})
export type PlannerPublishedEvent = z.infer<typeof plannerPublishedEventSchema>

export const plannerSourceSchema = z.object({
  planner_id: z.string().min(1),
  operation_id: z.union([z.string().min(1), z.null()]),
  version_id: z.union([z.string().min(1), z.null()]),
  expected_revision: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
})
export type PlannerSource = z.infer<typeof plannerSourceSchema>

export const plannerCandidateContextSchema = z.object({
  query_id: z.string().min(1),
  source: plannerSourceSchema,
  criteria_hash: z.string().min(1),
  queried_at: z.string().datetime({ offset: true }).regex(new RegExp('Z$')),
  expires_at: z.union([z.string().datetime({ offset: true }).regex(new RegExp('Z$')), z.null()]),
})
export type PlannerCandidateContext = z.infer<typeof plannerCandidateContextSchema>

export const plannerHotelCandidatesSchema = z.object({
  context: plannerCandidateContextSchema,
  city_stay_id: z.string().min(1),
  check_in_date: z.string().date(),
  check_out_date: z.string().date(),
  items: z.array(plannerHotelSelectionSchema),
  total_count: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  next_page: z.union([z.number().int().min(1).max(9007199254740991), z.null()]),
  warnings: z.array(plannerWarningSchema),
})
export type PlannerHotelCandidates = z.infer<typeof plannerHotelCandidatesSchema>

export const plannerHotelImageSchema = z.object({
  url: z.string(),
  name: z.union([z.string().min(1), z.null()]),
})
export type PlannerHotelImage = z.infer<typeof plannerHotelImageSchema>

export const plannerHotelAmenitySchema = z.object({
  name: z.string().min(1),
  status: z.enum(['unspecified', 'none', 'available', 'uncertain', 'partial']),
})
export type PlannerHotelAmenity = z.infer<typeof plannerHotelAmenitySchema>

export const plannerHotelPageDetailSchema = z.object({
  context: plannerCandidateContextSchema,
  selection: plannerHotelSelectionSchema,
  description: z.union([z.string().min(1), z.null()]),
  images: z.array(plannerHotelImageSchema),
  services: z.array(plannerHotelAmenitySchema),
  facilities: z.array(plannerHotelAmenitySchema),
  warnings: z.array(plannerWarningSchema),
})
export type PlannerHotelPageDetail = z.infer<typeof plannerHotelPageDetailSchema>

export const plannerHotelRoomCandidatesSchema = z.object({
  context: plannerCandidateContextSchema,
  hotel_candidate_ref: z.string().min(1),
  items: z.array(plannerRoomSelectionSchema),
  warnings: z.array(plannerWarningSchema),
})
export type PlannerHotelRoomCandidates = z.infer<typeof plannerHotelRoomCandidatesSchema>

export const plannerFlightCandidateSchema = z.object({
  flight: plannerFlightSelectionSchema,
  total_price: plannerMoneySchema,
  travelers: plannerTravelersSchema,
})
export type PlannerFlightCandidate = z.infer<typeof plannerFlightCandidateSchema>

export const plannerFlightCandidatesSchema = z.object({
  context: plannerCandidateContextSchema,
  items: z.array(plannerFlightCandidateSchema),
  warnings: z.array(plannerWarningSchema),
})
export type PlannerFlightCandidates = z.infer<typeof plannerFlightCandidatesSchema>

export const plannerFlightRuleItemSchema = z.object({
  change_fee: z.union([plannerMoneySchema, z.null()]),
  refund_fee: z.union([plannerMoneySchema, z.null()]),
  endorsement_description: z.union([z.string().min(1), z.null()]),
  rule_condition: z.union([z.string().min(1), z.null()]),
  rule_description: z.union([z.string().min(1), z.null()]),
})
export type PlannerFlightRuleItem = z.infer<typeof plannerFlightRuleItemSchema>

export const plannerFlightTicketRuleSchema = z.object({
  passenger_type: z.enum(['adult', 'child', 'infant']),
  segment_index: z.number().int().min(1).max(9007199254740991),
  items: z.array(plannerFlightRuleItemSchema),
})
export type PlannerFlightTicketRule = z.infer<typeof plannerFlightTicketRuleSchema>

export const plannerFlightBaggageRuleSchema = z.object({
  passenger_type: z.enum(['adult', 'child', 'infant']),
  segment_index: z.number().int().min(1).max(9007199254740991),
  pieces: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  weight: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  unit: z.union([z.string().min(1), z.null()]),
  rule_condition: z.union([z.string().min(1), z.null()]),
  rule_description: z.union([z.string().min(1), z.null()]),
})
export type PlannerFlightBaggageRule = z.infer<typeof plannerFlightBaggageRuleSchema>

export const plannerFlightPageDetailSchema = z.object({
  context: plannerCandidateContextSchema,
  item: plannerFlightCandidateSchema,
  ticket_rules: z.array(plannerFlightTicketRuleSchema),
  baggage_rules: z.array(plannerFlightBaggageRuleSchema),
  warnings: z.array(plannerWarningSchema),
})
export type PlannerFlightPageDetail = z.infer<typeof plannerFlightPageDetailSchema>

export const plannerReplaceHotelSchema = z.object({
  action: z.literal('REPLACE_HOTEL'),
  stay_id: z.string().min(1),
  candidate_ref: z.string().min(1),
})
export type PlannerReplaceHotel = z.infer<typeof plannerReplaceHotelSchema>

export const plannerReplaceFlightSchema = z.object({
  action: z.literal('REPLACE_FLIGHT'),
  transport_id: z.string().min(1),
  candidate_ref: z.string().min(1),
})
export type PlannerReplaceFlight = z.infer<typeof plannerReplaceFlightSchema>

export const plannerAddActivitySchema = z.object({
  action: z.literal('ADD_ACTIVITY'),
  day_id: z.string().min(1),
  position: z.number().int().min(0).max(9007199254740991),
  activity: plannerActivityInputSchema,
})
export type PlannerAddActivity = z.infer<typeof plannerAddActivitySchema>

export const plannerRemoveActivitySchema = z.object({
  action: z.literal('REMOVE_ACTIVITY'),
  activity_id: z.string().min(1),
})
export type PlannerRemoveActivity = z.infer<typeof plannerRemoveActivitySchema>

export const plannerReorderActivitiesSchema = z.object({
  action: z.literal('REORDER_ACTIVITIES'),
  day_id: z.string().min(1),
  activity_ids: z.array(z.string().min(1)),
})
export type PlannerReorderActivities = z.infer<typeof plannerReorderActivitiesSchema>

export const plannerMoveActivitySchema = z.object({
  action: z.literal('MOVE_ACTIVITY'),
  activity_id: z.string().min(1),
  target_day_id: z.string().min(1),
  position: z.number().int().min(0).max(9007199254740991),
})
export type PlannerMoveActivity = z.infer<typeof plannerMoveActivitySchema>

export const plannerEditSchema = z.object({
  expected_revision: z.number().int().min(0).max(9007199254740991),
  command: z.union([
    plannerReplaceHotelSchema,
    plannerReplaceFlightSchema,
    plannerAddActivitySchema,
    plannerRemoveActivitySchema,
    plannerReorderActivitiesSchema,
    plannerMoveActivitySchema,
  ]),
})
export type PlannerEdit = z.infer<typeof plannerEditSchema>

export const plannerEditResultSchema = z.object({
  version_id: z.string().min(1),
  revision: z.number().int().min(0).max(9007199254740991),
  changed_ids: z.array(z.string().min(1)),
  invalidated_ids: z.array(z.string().min(1)),
  warnings: z.array(plannerWarningSchema),
})
export type PlannerEditResult = z.infer<typeof plannerEditResultSchema>

export const plannerRestoreSchema = z.object({
  expected_latest_version_id: z.string().min(1),
  expected_latest_revision: z.number().int().min(0).max(9007199254740991),
  idempotency_key: z.string().min(1).max(128),
})
export type PlannerRestore = z.infer<typeof plannerRestoreSchema>

export const plannerSelectionRequestSchema = z.object({
  expected_revision: z.number().int().min(0).max(9007199254740991),
  candidate_ref: z.string().min(1),
})
export type PlannerSelectionRequest = z.infer<typeof plannerSelectionRequestSchema>

export const plannerErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  retryable: z.boolean(),
  operation_id: z.union([z.string().min(1), z.null()]),
  current_revision: z.union([z.number().int().min(0).max(9007199254740991), z.null()]),
  trace_id: z.union([z.string().min(1), z.null()]),
})
export type PlannerError = z.infer<typeof plannerErrorSchema>

export const plannerOperationSchema = z.object({
  operation_id: z.string().min(1),
  planner_id: z.string().min(1),
  kind: z.enum(['INITIAL', 'REPLAN', 'RESTORE']),
  status: z.enum(['RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELLED', 'TIMED_OUT']),
  started_at: z.string().datetime({ offset: true }).regex(new RegExp('Z$')),
  finished_at: z.union([z.string().datetime({ offset: true }).regex(new RegExp('Z$')), z.null()]),
  result_version_id: z.union([z.string().min(1), z.null()]),
  error: z.union([plannerErrorSchema, z.null()]),
})
export type PlannerOperation = z.infer<typeof plannerOperationSchema>
